import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { EnhancedThemeProvider } from "@/providers/enhanced-theme-provider";
import Dashboard from "@/pages/dashboard";
import VoterDetails from "@/pages/voter-details";
import FamilyPage from "@/pages/family";
import SurveysPage from "@/pages/surveys";
import LoginForm from "@/components/LoginForm";
import RoleBasedDashboard from "@/components/RoleBasedDashboard";
import NotFound from "@/pages/not-found";
import LanguageSelector from "@/components/LanguageSelector";
import TamilHelp from "@/components/TamilHelp";
import { MobileOptimizedContainer } from "@/components/ui/mobile-optimized-container";
import { OfflineIndicator } from "@/components/ui/offline-indicator";
import React, { StrictMode, Suspense, useEffect, useState } from "react";
import { useAuthStore, useAppStore } from "@/stores";
import { ErrorBoundary } from "react-error-boundary";
import { Loader2, HelpCircle, X, Wifi, WifiOff, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePerformance } from "@/hooks/use-performance";
import { useNetworkAware } from "@/hooks/use-network-aware";
import "./lib/i18n";
import { cn } from "@/lib/utils";

// Error boundary fallback component
function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
        <div className="text-red-500 text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
        <p className="text-gray-600 mb-4">{error.message}</p>
        <button
          onClick={resetErrorBoundary}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

// Loading component
function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="animate-spin h-8 w-8 text-blue-500 mx-auto mb-4" />
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

// App router component with authentication
function AppRouter() {
  const { user, isAuthenticated, isLoading, checkSession } = useAuthStore();
  const { theme } = useAppStore();
  const { metrics, isSlowNetwork, isLowMemory } = usePerformance();
  const { networkStatus, executeWithNetworkFallback } = useNetworkAware();

  // Check session on app startup
  useEffect(() => {
    if (!user && !isLoading) {
      checkSession();
    }
  }, [user, isLoading, checkSession]);

  // Performance optimization based on network conditions
  useEffect(() => {
    const document = document.documentElement;
    if (isSlowNetwork) {
      document.classList.add('slow-network');
    } else {
      document.classList.remove('slow-network');
    }

    if (isLowMemory) {
      document.classList.add('low-memory');
    } else {
      document.classList.remove('low-memory');
    }
  }, [isSlowNetwork, isLowMemory]);

  // Show loading while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<LoadingSpinner />}>
        <Switch>
          <Route path="/" component={() => {
            if (isAuthenticated && user) {
              return <RoleBasedDashboard user={user} />;
            }
            return <LoginForm />;
          }} />
          <Route path="/voter/:id" component={VoterDetails} />
          <Route path="/family" component={FamilyPage} />
          <Route path="/surveys" component={SurveysPage} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

// Main App component with React 19 features
function App() {
  const { theme } = useAppStore();
  const [showHelp, setShowHelp] = useState(false);
  const [showOfflineInfo, setShowOfflineInfo] = useState(false);
  const { isSlowNetwork, isLowMemory, metrics } = usePerformance();
  const { networkStatus, offlineDuration } = useNetworkAware();

  // Handle pull-to-refresh for the entire app
  const handlePullToRefresh = async () => {
    // Clear cache and reload
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      } catch (error) {
        console.log('Failed to clear cache:', error);
      }
    }
    window.location.reload();
  };

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <EnhancedThemeProvider defaultTheme={theme} disableTransitionOnChange>
          <TooltipProvider>
            <Toaster />

            {/* Mobile-optimized container with pull-to-refresh */}
            <MobileOptimizedContainer
              enablePullToRefresh={true}
              onPullToRefresh={handlePullToRefresh}
              maxWidth="full"
              padding="none"
            >
              <div className="min-h-screen flex flex-col">
                {/* Header with status indicators */}
                <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 sticky top-0 z-40">
                  <div className="container mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        VoterVerify
                      </div>

                      {/* Performance indicators */}
                      <div className="flex items-center gap-1">
                        {isSlowNetwork && (
                          <div className="flex items-center gap-1 text-orange-500">
                            <Wifi className="h-3 w-3" />
                            <span className="text-xs hidden sm:inline">Slow</span>
                          </div>
                        )}
                        {isLowMemory && (
                          <div className="flex items-center gap-1 text-red-500">
                            <Download className="h-3 w-3" />
                            <span className="text-xs hidden sm:inline">Low Mem</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Network status */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowOfflineInfo(!showOfflineInfo)}
                        className={cn(
                          "flex items-center gap-1",
                          !networkStatus.online && "border-orange-500 text-orange-500"
                        )}
                      >
                        <Wifi className={cn("h-4 w-4", !networkStatus.online && "animate-pulse")} />
                        <span className="hidden sm:inline">
                          {networkStatus.online ? 'Online' : 'Offline'}
                        </span>
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowHelp(true)}
                        className="flex items-center gap-2"
                      >
                        <HelpCircle className="h-4 w-4" />
                        <span className="hidden sm:inline">Help</span>
                      </Button>
                      <LanguageSelector />
                    </div>
                  </div>

                  {/* Network status banner */}
                  {!networkStatus.online && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 border-t border-orange-200 dark:border-orange-800 px-4 py-2">
                      <div className="container mx-auto flex items-center justify-between">
                        <div className="flex items-center gap-2 text-orange-800 dark:text-orange-200 text-sm">
                          <WifiOff className="h-4 w-4" />
                          <span>You're offline. Some features may be limited.</span>
                          {offlineDuration > 0 && (
                            <span className="text-xs">({Math.round(offlineDuration / 60000)}m ago)</span>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.reload()}
                          className="h-6 text-xs"
                        >
                          Retry
                        </Button>
                      </div>
                    </div>
                  )}
                </header>

                {/* Main content */}
                <main className="flex-1">
                  <AppRouter />
                </main>

                {/* Help Modal */}
                {showHelp && (
                  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
                      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex justify-between items-center">
                        <h2 className="text-xl font-semibold">Tamil Language Help</h2>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowHelp(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="p-4">
                        <TamilHelp />
                      </div>
                    </div>
                  </div>
                )}

                {/* Offline Info Modal */}
                {showOfflineInfo && (
                  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full">
                      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                        <h3 className="text-lg font-semibold">Connection Status</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowOfflineInfo(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="p-4">
                        <OfflineIndicator showDetails={true} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Performance Debug Info (development only) */}
                {process.env.NODE_ENV === 'development' && metrics && (
                  <div className="fixed bottom-4 left-4 bg-black/80 text-white text-xs p-2 rounded z-50">
                    <div>FCP: {metrics.firstContentfulPaint?.toFixed(0)}ms</div>
                    <div>Memory: {metrics.memoryUsage ? (metrics.memoryUsage / 1024 / 1024).toFixed(1) : 'N/A'}MB</div>
                    <div>Network: {networkStatus.effectiveType}</div>
                  </div>
                )}
              </div>
            </MobileOptimizedContainer>
          </TooltipProvider>
        </EnhancedThemeProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}

export default App;
